import { useEffect } from 'react'

import { ddLogger, usePusher } from '@b-stock/fe-core/bstock-react'

import appConfig from '@config/config'
import { usePerson } from '@store/data/modules/person'

import { useManagedAccessToken } from './AuthProvider'

const PUSHER_TOKEN_REFRESH_EVENT = 'token-refresh'

type InnerProps = {
  channelName: string
  personId: string
  accountId: string
}

function PusherTokenRefreshSetupInner({
  channelName,
  personId,
  accountId,
}: InnerProps) {
  const { appKey, cluster } = appConfig.pusher
  const pusherClient = usePusher(appKey, cluster)
  const managedAccessToken = useManagedAccessToken()

  useEffect(() => {
    if (!pusherClient || !managedAccessToken) return

    ddLogger.info('Subscribing to Pusher token refresh channel', {
      channel: channelName,
      personId,
      accountId,
    })

    const channel = pusherClient.subscribe(channelName)

    const handleTokenRefresh = async () => {
      ddLogger.debug('Token refresh event received from Pusher', {
        channel: channelName,
      })
      try {
        const result = await managedAccessToken.refreshToken()
        if (result) {
          ddLogger.debug('Token refreshed successfully after Pusher event', {
            channel: channelName,
          })
        } else {
          ddLogger.debug('Token not refreshed after Pusher event', {
            channel: channelName,
          })
        }
      } catch (error) {
        ddLogger.error('Failed to refresh token after Pusher event', {
          channel: channelName,
          error: error instanceof Error ? error.message : String(error),
        })
      }
    }

    channel.bind(PUSHER_TOKEN_REFRESH_EVENT, handleTokenRefresh)

    return () => {
      channel.unbind(PUSHER_TOKEN_REFRESH_EVENT, handleTokenRefresh)
      pusherClient.unsubscribe(channelName)
    }
  }, [pusherClient, managedAccessToken, channelName, personId, accountId])

  return null
}

export function PusherTokenRefreshSetup() {
  const { appKey, cluster } = appConfig.pusher
  const { data: person } = usePerson()

  const channelName = person?.pusherChannels?.privateAccountChannelName

  if (!appKey || !cluster || !channelName) return null

  return (
    <PusherTokenRefreshSetupInner
      channelName={channelName}
      personId={person._id}
      accountId={person.accountId}
    />
  )
}
